import { Market, MarketParams, MathLib } from '@morpho-org/blue-sdk'
import { MorphoBlueAbi } from '@repo/contracts'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@repo/ui'
import { abbreviateAddress } from '@repo/utils'
import {
  EtherscanLink,
  MorphoLink,
  useChainId,
  useReadContract,
  useTokenAllowances,
  useWaitForTransactionReceipt,
  useWriteContract
} from '@repo/web3'
import { isNil } from 'lodash-es'
import { ExternalLink } from 'lucide-react'
import {
  createContext,
  type PropsWithChildren,
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { type Address, erc20Abi, type Hash, parseUnits } from 'viem'

import type { LiquidationPosition } from '@/lib/shared/types'

import { formatters, formatUint256, formatUint256Percent } from '@/lib/shared/utils/formatters'
import { getMorphoAddresses } from '@/lib/shared/utils/morpho'

import { TokenPairCell } from './table/cells'

type Step = 'Approve' | 'Liquidate' | 'Receipt'
const APPROVAL_BUFFER = parseUnits('1.00005', 18) // 0.005% approval buffer

/**
 * Reconstruct a Market instance from the serialized position data.
 * This is needed for the approval amount calculation (getLiquidationRepaidShares + toBorrowAssets).
 */
function reconstructMarket(position: LiquidationPosition): Market {
  return new Market({
    params: new MarketParams(position.marketParams),
    totalSupplyAssets: position.marketState.totalSupplyAssets,
    totalSupplyShares: position.marketState.totalSupplyShares,
    totalBorrowAssets: position.marketState.totalBorrowAssets,
    totalBorrowShares: position.marketState.totalBorrowShares,
    lastUpdate: position.marketState.lastUpdate,
    fee: position.marketState.fee,
    price: position.marketState.price
  })
}

// Liquidation Modal Context Setup
const LiquidationModalContext = createContext({
  position: {} as LiquidationPosition,
  step: 'Approve' as Step,
  liquidatorAddress: '' as Address,
  nextStep: () => {},
  reset: () => {},
  liquidationHash: undefined as Hash | undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setLiquidationHash: (hash: Hash) => {}
})
function LiquidationModalProvider({
  children,
  position,
  liquidatorAddress
}: PropsWithChildren<{
  position: LiquidationPosition
  liquidatorAddress: Address
}>): ReactElement {
  const [step, setStep] = useState<Step>('Approve')
  const [liquidationHash, setLiquidationHash] = useState<Hash>()

  const nextStep = useCallback(() => {
    setStep(prev => (prev === 'Approve' ? 'Liquidate' : 'Receipt'))
  }, [])

  const reset = useCallback(() => {
    setStep('Approve')
    setLiquidationHash(undefined)
  }, [])

  return (
    <LiquidationModalContext.Provider
      value={{
        position,
        liquidatorAddress,
        step,
        nextStep,
        liquidationHash,
        setLiquidationHash,
        reset
      }}
    >
      {children}
    </LiquidationModalContext.Provider>
  )
}

type LiquidationModalProps = {
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  position: LiquidationPosition
  liquidatorAddress: Address
  onLiquidation?: (hash: Hash) => void
}

// Liquidation Modal Entry Point
export function LiquidationModal({
  isOpen,
  onOpenChange,
  position,
  liquidatorAddress,
  onLiquidation
}: LiquidationModalProps): ReactElement {
  return (
    <LiquidationModalProvider position={position} liquidatorAddress={liquidatorAddress}>
      <LiquidationModalBase
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onLiquidation={onLiquidation}
      />
    </LiquidationModalProvider>
  )
}

// Displays shared position details and renders the component for the current step's action.
function LiquidationModalBase({
  isOpen,
  onOpenChange,
  onLiquidation
}: {
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  onLiquidation?: (hash: Hash) => void
}): ReactElement {
  const chainId = useChainId()
  const { position, step, reset } = useContext(LiquidationModalContext)

  const collateralSymbol = position.collateralTokenMetadata.symbol
  const loanSymbol = position.loanTokenMetadata.symbol

  const renderStep = () => {
    switch (step) {
      case 'Approve':
        return <ApprovalStep />
      case 'Liquidate':
        return <LiquidateStep onLiquidation={onLiquidation} />
      case 'Receipt':
        return <ReceiptStep />
    }
  }

  // reset when the modal opens (transitions from closed to open)
  const prevIsOpenRef = useRef(isOpen)
  useEffect(() => {
    const prevIsOpen = prevIsOpenRef.current
    prevIsOpenRef.current = isOpen

    // Only reset when modal opens (transitions from false/undefined to true)
    if (isOpen && !prevIsOpen) {
      reset()
    }
  }, [isOpen, reset])

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Liquidate Position</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <div className="bg-muted rounded-lg p-3">
            <Table className="border-0 bg-transparent">
              <TableBody className="border-0">
                <TableRow className="border-0">
                  <TableCell className="w-40 border-0 bg-transparent py-2 font-medium capitalize">
                    Market
                  </TableCell>
                  <TableCell className="overflow-auto border-0 bg-transparent py-2">
                    <div className="flex items-center gap-2">
                      <TokenPairCell collateralSymbol={collateralSymbol} loanSymbol={loanSymbol} />
                      <div className="flex flex-col">
                        <p
                          className="max-w-28 overflow-hidden text-xs text-ellipsis whitespace-nowrap"
                          title={`${collateralSymbol} / ${loanSymbol}`}
                        >
                          {collateralSymbol} / {loanSymbol}
                        </p>
                        <MorphoLink
                          className="w-min text-xs"
                          marketId={position.marketId}
                          chainId={chainId}
                        >
                          {abbreviateAddress(position.marketId)}
                        </MorphoLink>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow className="border-0">
                  <TableCell className="w-40 border-0 bg-transparent py-2 font-medium capitalize">
                    User
                  </TableCell>
                  <TableCell className="border-0 bg-transparent py-2">
                    <EtherscanLink address={position.user} chainId={chainId}>
                      {abbreviateAddress(position.user)}
                    </EtherscanLink>
                  </TableCell>
                </TableRow>
                <TableRow className="border-0">
                  <TableCell className="w-40 border-0 bg-transparent py-2 font-medium capitalize">
                    Borrow Amount
                  </TableCell>
                  <TableCell className="border-0 bg-transparent py-2">
                    {formatUint256(position.borrowAssets, position.loanTokenMetadata.decimals)}{' '}
                    {loanSymbol}
                  </TableCell>
                </TableRow>
                <TableRow className="border-0">
                  <TableCell className="w-40 border-0 bg-transparent py-2 font-medium capitalize">
                    Collateral Amount
                  </TableCell>
                  <TableCell className="border-0 bg-transparent py-2">
                    {formatUint256(position.collateral, position.collateralTokenMetadata.decimals)}{' '}
                    {collateralSymbol}
                  </TableCell>
                </TableRow>
                <TableRow className="border-0">
                  <TableCell className="w-40 border-0 bg-transparent py-2 font-medium capitalize">
                    LIF
                  </TableCell>
                  <TableCell className="border-0 bg-transparent py-2">
                    {formatUint256Percent(position.liquidationIncentiveFactor)}
                  </TableCell>
                </TableRow>
                <TableRow className="border-0">
                  <TableCell className="w-40 border-0 bg-transparent py-2 font-medium capitalize">
                    Estimated Profit
                  </TableCell>
                  <TableCell className="border-0 bg-transparent py-2">
                    {position.estimatedProfit === null
                      ? '—'
                      : formatters.commas
                          .readable()
                          .unit('$')
                          .digits(2)
                          .min(0.01)
                          .of(position.estimatedProfit, 18)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
        <DialogFooter>{renderStep()}</DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Handles the ERC20 approval step for the loan token, if necessary.
function ApprovalStep(): ReactElement {
  const { nextStep, position, liquidatorAddress, step } = useContext(LiquidationModalContext)
  const chainId = useChainId()
  const { writeContract, isPending: isSubmitting, data: hash } = useWriteContract()
  const { isLoading: isTxPending, isSuccess: isTxSuccess } = useWaitForTransactionReceipt(
    hash,
    chainId
  )

  const morphoAddress = getMorphoAddresses(chainId).morpho

  // Fetch allowance/balance, calculate required funds, and check sufficiency.
  const { allowanceFor, isAllowancesLoading } = useTokenAllowances({
    chainId,
    spenderAddress: morphoAddress,
    userAddress: liquidatorAddress,
    tokenAddresses: [position.marketParams.loanToken]
  })

  const { data: balance, isLoading: isBalanceLoading } = useReadContract({
    abi: erc20Abi,
    address: position.marketParams.loanToken.toLowerCase() as Address,
    functionName: 'balanceOf',
    args: [liquidatorAddress.toLowerCase() as Address]
  })

  const fundsNeeded = useMemo(() => {
    if (!position.seizableCollateral) return 0n
    const market = reconstructMarket(position)
    const repaidShares = market.getLiquidationRepaidShares(position.seizableCollateral)
    return repaidShares
      ? MathLib.wMulUp(market.toBorrowAssets(repaidShares, 'Up'), APPROVAL_BUFFER)
      : 0n
  }, [position])

  const limiter = useMemo(() => {
    if (isNil(balance) || isNil(allowanceFor(position.marketParams.loanToken))) return

    if (balance < fundsNeeded) {
      return 'balance'
    }

    if (allowanceFor(position.marketParams.loanToken) < fundsNeeded) {
      return 'allowance'
    }
    return 'none'
  }, [balance, allowanceFor, position.marketParams.loanToken, fundsNeeded])

  // Advance to next step only after approval transaction is confirmed successful.
  useEffect(() => {
    // if allowance is not needed, move to next step immediately
    if (step === 'Approve' && limiter === 'none') {
      nextStep()
      return
    }
    if (isTxSuccess) {
      nextStep()
    }
  }, [isTxSuccess, nextStep, limiter, step])

  // Handles submitting the ERC20 approval transaction.
  const handleApprove = () => {
    if (!limiter) return
    if (limiter === 'balance') return
    if (limiter === 'none') {
      nextStep()
      return
    }

    // `chainId` pins the write to the chain these addresses were resolved for. `Web3Provider` sets
    // `allowUnsupportedChain`, so the wallet's chain can differ from `useChainId()` — without this,
    // the approve would target `loanToken`'s address on whatever chain the wallet is on.
    writeContract({
      abi: erc20Abi,
      address: position.marketParams.loanToken,
      chainId,
      functionName: 'approve',
      args: [morphoAddress, fundsNeeded]
    })
  }

  if (isAllowancesLoading || isBalanceLoading) return <Button className="w-full" isLoading />
  if (limiter === 'balance')
    return (
      <Button className="w-full" disabled>
        Not enough funds to liquidate
      </Button>
    )

  return (
    <Button
      data-testid="liquidation-approve"
      variant="default"
      className="w-full"
      onClick={handleApprove}
      disabled={isSubmitting || isTxPending} // Loading during submission and confirmation
    >
      {isSubmitting || isTxPending ? (
        <>
          <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
          Processing...
        </>
      ) : (
        `Approve ${position.loanTokenMetadata.symbol}`
      )}
    </Button>
  )
}

// Handles the liquidation transaction submission and tracks its status.
function LiquidateStep({ onLiquidation }: { onLiquidation?: (hash: Hash) => void }): ReactElement {
  const chainId = useChainId()
  const morphoAddress = getMorphoAddresses(chainId).morpho
  const { nextStep, position, setLiquidationHash } = useContext(LiquidationModalContext)
  const { writeContract, isPending: isSubmitting, data: hash } = useWriteContract()
  const {
    data: receipt,
    isLoading: isTxPending,
    isSuccess: isTxSuccess
  } = useWaitForTransactionReceipt(hash, chainId)

  // Sync the receipt hash with the context.
  useEffect(() => {
    if (receipt) {
      setLiquidationHash(receipt.transactionHash)
    }
  }, [receipt, setLiquidationHash])

  // Move to next step if the liquidation transaction is successful.
  useEffect(() => {
    if (isTxSuccess && hash) {
      onLiquidation?.(receipt.transactionHash)
      nextStep()
    }
  }, [hash, isTxSuccess, nextStep, onLiquidation, receipt?.transactionHash])

  // Handles the Morpho liquidate transaction submission.
  const handleLiquidation = () => {
    // See the approve step: `chainId` must pin the write, or wagmi submits to the wallet's chain
    // while `morphoAddress` and `marketParams` came from `useChainId()`.
    writeContract({
      abi: MorphoBlueAbi,
      address: morphoAddress,
      chainId,
      functionName: 'liquidate',
      args: [{ ...position.marketParams }, position.user, position.seizableCollateral, 0n, '0x']
    })
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="text-muted-foreground text-center text-xs">
        By confirming this transaction, you agree to our{' '}
        <a
          href="https://morpho.org/terms-of-use/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Terms of Use
        </a>
      </p>
      <Button
        data-testid="liquidation-confirm"
        variant="default"
        className="w-full"
        onClick={handleLiquidation}
        disabled={isSubmitting || isTxPending || !position.seizableCollateral}
      >
        {isSubmitting || isTxPending ? (
          <>
            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
            Processing...
          </>
        ) : (
          'Liquidate Position'
        )}
      </Button>
    </div>
  )
}

// Displays the final success message and a link to the transaction on Etherscan.
function ReceiptStep(): ReactElement {
  const chainId = useChainId()
  const { liquidationHash } = useContext(LiquidationModalContext)

  if (!liquidationHash) {
    return (
      <Button className="w-full" disabled>
        Liquidation processing...
      </Button>
    ) // Handle case where hash might not be ready yet
  }

  return (
    <div className="w-full">
      <Button asChild className="w-full">
        <EtherscanLink tx={liquidationHash} chainId={chainId}>
          View Successful Transaction
          <ExternalLink className="ml-2" />
        </EtherscanLink>
      </Button>
    </div>
  )
}
